import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  ShieldCheck, Plus, Trash, PencilSimple, Copy, Buildings,
  House, Circuitry, Users, CurrencyCircleDollar, GearSix, Spinner,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_ICONS = {
  "Apartman": Buildings,
  "Daire": House,
  "Sayac": Circuitry,
  "Kiraci": Users,
  "Finansal": CurrencyCircleDollar,
  "Sistem": GearSix,
  "Sabit Gider": CurrencyCircleDollar,
};

const GROUP_COLORS = {
  "Apartman": "text-indigo-600 bg-indigo-50",
  "Daire": "text-emerald-600 bg-emerald-50",
  "Sayac": "text-amber-600 bg-amber-50",
  "Kiraci": "text-purple-600 bg-purple-50",
  "Finansal": "text-rose-600 bg-rose-50",
  "Sistem": "text-sky-600 bg-sky-50",
  "Sabit Gider": "text-orange-600 bg-orange-50",
};

const PermissionTemplatesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const apiBase = isAdmin ? `${API_URL}/api/admin` : `${API_URL}/api/customer`;
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formPerms, setFormPerms] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tRes, gRes] = await Promise.all([
        axios.get(`${apiBase}/permission-templates`),
        axios.get(`${API_URL}/api/admin/permission-groups`),
      ]);
      setTemplates(tRes.data);
      setGroups(gRes.data.groups);
      setDefaults(gRes.data.defaults);
    } catch { toast.error(t("permission.data_error")); }
    finally { setLoading(false); }
  }, [apiBase, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditingTemplate(null);
    setFormName("");
    setFormDesc("");
    setFormPerms({ ...defaults });
    setDialogOpen(true);
  };

  const openEdit = (tmpl) => {
    setEditingTemplate(tmpl);
    setFormName(tmpl.name);
    setFormDesc(tmpl.description || "");
    setFormPerms({ ...defaults, ...tmpl.permissions });
    setDialogOpen(true);
  };

  const openDuplicate = (tmpl) => {
    setEditingTemplate(null);
    setFormName(`${tmpl.name} (${t("permission.copy_suffix")})`);
    setFormDesc(tmpl.description || "");
    setFormPerms({ ...defaults, ...tmpl.permissions });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error(t("permission.name_required")); return; }
    setSaving(true);
    try {
      const payload = { name: formName, description: formDesc, permissions: formPerms };
      if (editingTemplate) {
        await axios.put(`${apiBase}/permission-templates/${editingTemplate.id}`, payload);
        toast.success(t("permission.updated"));
      } else {
        await axios.post(`${apiBase}/permission-templates`, payload);
        toast.success(t("permission.created"));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t("permission.save_error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (tmpl) => {
    if (!window.confirm(t("permission.confirm_delete"))) return;
    try {
      await axios.delete(`${apiBase}/permission-templates/${tmpl.id}`);
      toast.success(t("permission.deleted"));
      fetchData();
    } catch { toast.error(t("permission.delete_error")); }
  };

  const toggleGroup = (groupName, value) => {
    const groupPerms = groups[groupName];
    if (!groupPerms) return;
    const newPerms = { ...formPerms };
    groupPerms.forEach((p) => { newPerms[p.key] = value; });
    setFormPerms(newPerms);
  };

  const toggleAll = (value) => {
    const newPerms = {};
    Object.keys(defaults).forEach((key) => { newPerms[key] = value; });
    setFormPerms(newPerms);
  };

  const getGroupLabel = (groupName) => {
    const labels = {
      "Apartman": t("permission.group_apartment"),
      "Daire": t("permission.group_unit"),
      "Sayac": t("permission.group_meter"),
      "Kiraci": t("permission.group_tenant"),
      "Finansal": t("permission.group_financial"),
      "Sistem": t("permission.group_system"),
      "Sabit Gider": t("permission.group_fixed_expense"),
    };
    return labels[groupName] || groupName;
  };

  const activeCount = (perms) => Object.values(perms || {}).filter(Boolean).length;
  const totalCount = Object.keys(defaults).length;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="permission-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-900">{t("permission.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("permission.subtitle")}</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-template-btn">
          <Plus size={18} className="mr-2" /> {t("permission.new")}
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="p-8 text-center">
            <ShieldCheck size={48} className="mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-medium text-slate-600 mb-1">{t("permission.no_templates")}</h3>
            <p className="text-sm text-slate-400 mb-4">{t("permission.no_templates_desc")}</p>
            <Button onClick={openCreate} variant="outline">
              <Plus size={16} className="mr-2" /> {t("permission.create_first")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((tmpl) => (
            <Card key={tmpl.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`template-card-${tmpl.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <ShieldCheck size={18} className="text-indigo-600" />
                    {tmpl.name}
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">
                    {activeCount(tmpl.permissions)}/{totalCount}
                  </Badge>
                </div>
                {tmpl.description && <p className="text-xs text-slate-500 mt-1">{tmpl.description}</p>}
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {Object.entries(groups).map(([groupName, groupPerms]) => {
                    const groupActive = groupPerms.every((p) => tmpl.permissions?.[p.key]);
                    const groupPartial = groupPerms.some((p) => tmpl.permissions?.[p.key]);
                    return (
                      <Badge key={groupName} className={`text-[10px] ${
                        groupActive ? "bg-emerald-100 text-emerald-700" :
                        groupPartial ? "bg-amber-100 text-amber-700" :
                        "bg-slate-100 text-slate-500"
                      }`}>
                        {getGroupLabel(groupName)}: {groupActive ? t("permission.full") : groupPartial ? t("permission.partial") : t("permission.none")}
                      </Badge>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => openEdit(tmpl)} data-testid={`edit-template-${tmpl.id}`}>
                    <PencilSimple size={14} className="mr-1" /> {t("common.edit")}
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => openDuplicate(tmpl)} data-testid={`dup-template-${tmpl.id}`}>
                    <Copy size={14} />
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleDelete(tmpl)} data-testid={`del-template-${tmpl.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingTemplate ? t("permission.edit_title") : t("permission.create_title")}</DialogTitle>
            <DialogDescription>{t("permission.template_desc")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("permission.template_name")}</Label>
                <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Ornek: Premium" data-testid="template-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("common.description")}</Label>
                <Input value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="Tam yetkili paket" data-testid="template-desc-input" />
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex gap-2 border-b border-slate-200 pb-3">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggleAll(true)} data-testid="toggle-all-on">{t("permission.toggle_all_on")}</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => toggleAll(false)} data-testid="toggle-all-off">{t("permission.toggle_all_off")}</Button>
              <span className="text-xs text-slate-500 flex items-center ml-auto">
                {activeCount(formPerms)}/{totalCount} {t("common.active").toLowerCase()}
              </span>
            </div>

            {/* Permission groups */}
            {Object.entries(groups).map(([groupName, groupPerms]) => {
              const Icon = GROUP_ICONS[groupName] || GearSix;
              const colors = GROUP_COLORS[groupName] || "text-slate-600 bg-slate-50";
              const [textColor, bgColor] = colors.split(" ");
              const allOn = groupPerms.every((p) => formPerms[p.key]);
              return (
                <div key={groupName} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${bgColor}`}>
                        <Icon size={16} className={textColor} />
                      </div>
                      <span className="text-sm font-semibold text-slate-800">{getGroupLabel(groupName)}</span>
                    </div>
                    <Button size="sm" variant="ghost" className="h-6 text-xs text-slate-500"
                      onClick={() => toggleGroup(groupName, !allOn)}>
                      {allOn ? t("permission.group_off") : t("permission.group_on")}
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-2">
                    {groupPerms.map((perm) => (
                      <div key={perm.key} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg" data-testid={`perm-toggle-${perm.key}`}>
                        <div className="min-w-0 mr-3">
                          <p className="text-sm font-medium text-slate-800 truncate">{perm.label}</p>
                          <p className="text-[11px] text-slate-500 truncate">{perm.desc}</p>
                        </div>
                        <Switch
                          checked={formPerms[perm.key] ?? false}
                          onCheckedChange={(val) => setFormPerms({ ...formPerms, [perm.key]: val })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-template-btn">
                {saving ? <Spinner size={16} className="animate-spin mr-2" /> : null}
                {editingTemplate ? t("common.update") : t("common.create")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionTemplatesPage;
